/**
 * Cookie-based snooze for the temporary contract upgrade CRM redirect.
 * Value: epoch milliseconds when the prompt may show again.
 */
export const CONTRACT_UPGRADE_SNOOZE_COOKIE =
  "sw_contract_upgrade_snooze_until";

/** Production snooze: 36 hours before showing the upgrade prompt again. */
const CONTRACT_UPGRADE_SNOOZE_SECONDS_PRODUCTION = 36 * 60 * 60;

/** Dev-only short snooze so dismiss behavior can be exercised quickly. */
const CONTRACT_UPGRADE_SNOOZE_SECONDS_DEV = 30;

/**
 * Default snooze duration for the CRM upgrade redirect after dismiss.
 * Dev server uses a short window; production builds use 36 hours.
 */
export const CONTRACT_UPGRADE_SNOOZE_SECONDS_DEFAULT = import.meta.env.DEV
  ? CONTRACT_UPGRADE_SNOOZE_SECONDS_DEV
  : CONTRACT_UPGRADE_SNOOZE_SECONDS_PRODUCTION;

function readCookieRaw(name: string): string | null {
  if (typeof document === "undefined") return null;
  const prefix = `${encodeURIComponent(name)}=`;
  const parts = document.cookie.split("; ");
  for (const part of parts) {
    if (part.startsWith(prefix)) {
      return decodeURIComponent(part.slice(prefix.length));
    }
  }
  return null;
}

/**
 * Return the timestamp (ms) after which the upgrade prompt may show again, or null if not snoozed / invalid.
 */
export function getContractUpgradeSnoozeUntilMs(): number | null {
  const raw = readCookieRaw(CONTRACT_UPGRADE_SNOOZE_COOKIE);
  if (!raw) return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

/**
 * True if snooze is still active (now is before snooze-until).
 */
export function isContractUpgradeSnoozeActive(
  nowMs: number = Date.now()
): boolean {
  const until = getContractUpgradeSnoozeUntilMs();
  if (until === null) return false;
  return nowMs < until;
}

/**
 * Set snooze cookie; browser will drop it after maxAgeSeconds.
 */
export function setContractUpgradeSnoozeCookie(
  maxAgeSeconds: number = CONTRACT_UPGRADE_SNOOZE_SECONDS_DEFAULT
): void {
  if (typeof document === "undefined") return;
  const until = Date.now() + maxAgeSeconds * 1000;
  const encoded = encodeURIComponent(CONTRACT_UPGRADE_SNOOZE_COOKIE);
  const value = encodeURIComponent(String(until));
  document.cookie = `${encoded}=${value}; Path=/; Max-Age=${maxAgeSeconds}; SameSite=Lax`;
}
